import { useEffect, useMemo, useState } from 'react';
import {
  fetchMobileTopChartsTagDetailsMapping,
  resolveTableRecordGameId,
} from '@intelligence/v3/utils/sensorTowerTagColumns';

interface IUseMobileTopChartsTagDetailsOptions {
  /** 为 false 时不请求（如 PC/Console 榜单） */
  enabled?: boolean;
}

/**
 * 按表格行拉取 Sensor Tower / App Magic 标签详情，供 getTagFilterColumns 使用。
 * 与 ActiveUsers（useTopChartsCore.updateGameDetailsData）字段一致，仅保留标签列所需字段。
 */
export default function useMobileTopChartsTagDetails(
  records: any[],
  options?: IUseMobileTopChartsTagDetailsOptions,
) {
  const enabled = options?.enabled ?? true;

  const gameIds = useMemo(() => {
    if (!enabled || !Array.isArray(records)) {
      return [];
    }
    return [...new Set(records.map((item) => resolveTableRecordGameId(item)).filter(Boolean))];
  }, [enabled, records]);

  const gameIdsKey = gameIds.join(',');

  const [gameDetailsLoading, setGameDetailsLoading] = useState(enabled);
  const [gameDetailsMapping, setGameDetailsMapping] = useState<IRecord<IRecord>>({});

  useEffect(() => {
    if (!enabled) {
      setGameDetailsLoading(false);
      setGameDetailsMapping({});
      return;
    }

    if (gameIds.length === 0) {
      setGameDetailsLoading(false);
      setGameDetailsMapping({});
      return;
    }

    let cancelled = false;
    setGameDetailsLoading(true);

    fetchMobileTopChartsTagDetailsMapping(gameIds)
      .then((mapping) => {
        if (!cancelled) {
          setGameDetailsMapping(mapping);
        }
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setGameDetailsMapping({});
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGameDetailsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, gameIdsKey]);

  return { gameDetailsLoading, gameDetailsMapping };
}
